import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, MapPin } from 'lucide-react';
import EmberField from './EmberField';
import { byslug } from '../data/media';

const HERO = 'villa-night-full';

const ParallaxHero = ({ onBook, onGallery }) => {
  const sceneRef = useRef(null);
  const layersRef = useRef({});
  const [loaded, setLoaded] = useState(false);
  const image = byslug(HERO);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    const scene = sceneRef.current;
    if (!scene) return undefined;

    let pointerX = 0;
    let pointerY = 0;
    let currentX = 0;
    let currentY = 0;
    let scrollY = 0;
    let raf = 0;

    const onPointer = (e) => {
      const rect = scene.getBoundingClientRect();
      pointerX = (e.clientX - rect.left) / rect.width - 0.5;
      pointerY = (e.clientY - rect.top) / rect.height - 0.5;
    };
    const onLeave = () => {
      pointerX = 0;
      pointerY = 0;
    };
    const onScroll = () => {
      scrollY = window.scrollY;
    };

    const tick = () => {
      // Ease toward the pointer so motion feels weighted, not twitchy.
      currentX += (pointerX - currentX) * 0.06;
      currentY += (pointerY - currentY) * 0.06;

      const { media, glow, content } = layersRef.current;
      const depth = Math.min(scrollY, 900);

      if (media) {
        media.style.transform =
          `translate3d(${currentX * -26}px, ${currentY * -18 + depth * 0.18}px, 0) scale(${1.08 + depth * 0.00012})`;
      }
      if (glow) {
        glow.style.transform = `translate3d(${currentX * -52}px, ${currentY * -34}px, 0)`;
      }
      if (content) {
        content.style.transform = `translate3d(${currentX * 16}px, ${currentY * 10 - depth * 0.12}px, 0)`;
        content.style.opacity = String(Math.max(0, 1 - depth / 620));
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    scene.addEventListener('pointerleave', onLeave);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('scroll', onScroll);
      scene.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  const setLayer = (name) => (el) => {
    layersRef.current[name] = el;
  };

  return (
    <section className="night-hero" ref={sceneRef}>
      <div className="night-hero-media" ref={setLayer('media')}>
        <img
          src={image.lqip}
          alt=""
          aria-hidden="true"
          className={`night-hero-lqip ${loaded ? 'is-hidden' : ''}`}
        />
        <picture>
          <source srcSet={`${image.webpSmall} 640w, ${image.webp} 1200w, ${image.webpLarge} 2400w`} type="image/webp" sizes="100vw" />
          <img
            src={image.src}
            alt="Leoland by VengaFarms lit up after dark"
            className={`night-hero-img ${loaded ? 'is-loaded' : ''}`}
            onLoad={() => setLoaded(true)}
            fetchPriority="high"
          />
        </picture>
      </div>

      <div className="night-hero-glow" ref={setLayer('glow')} aria-hidden="true" />
      <div className="night-hero-scrim" aria-hidden="true" />
      <EmberField className="night-hero-embers" density={30} />

      <div className="night-hero-content edge" ref={setLayer('content')}>
        <div className="wrap">
          <span className="label">Farmhouse for rent &middot; Shamirpet, Hyderabad</span>
          <h1 className="night-hero-title">
            <span className="line"><span>Stay up</span></span>
            <span className="line"><span>a little</span></span>
            <span className="line accent"><span>later.</span></span>
          </h1>
          <p className="night-hero-sub">
            A four-bedroom farmhouse with a private pool, a bonfire pit and
            twelve beds, on its own lawn inside Leonia Resorts &mdash; forty
            minutes from Hyderabad, and nothing like it nearby.
          </p>
          <div className="night-hero-actions">
            <button className="btn btn-glow" onClick={onBook}>
              Check availability <ArrowUpRight size={16} />
            </button>
            <button className="btn btn-line-dark" onClick={onGallery}>
              See the property
            </button>
          </div>
          <div className="night-hero-meta">
            <MapPin size={14} />
            <span>Plot 429, Leonia Resorts, Shamirpet, Hyderabad</span>
          </div>
        </div>
      </div>

      <div className="night-hero-cue" aria-hidden="true">
        <span>Scroll</span>
        <i />
      </div>
    </section>
  );
};

export default ParallaxHero;
